import React, { useState, useEffect, useMemo } from 'react';
import { Box, Container, Typography, Tabs, Tab, Paper, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Properties } from './Properties';
import { Loans } from './Loans';
import { BankAccounts } from './BankAccounts';
import { Companies } from './Companies';
import { Pensions } from './Pensions';
import { UserManagement } from './UserManagement';
import { Families } from './Families';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`entities-tabpanel-${index}`}
      aria-labelledby={`entities-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `entities-tab-${index}`,
    'aria-controls': `entities-tabpanel-${index}`,
  };
}

export const Entities: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Define all possible tabs with their RBAC requirements
  const allTabs = useMemo(() => [
    { 
      id: 0, 
      label: 'Users', 
      icon: '👥', 
      component: UserManagement,
      requiredPermission: 'can_manage_families', // Admin only for now
      path: '/entities/users'
    },
    { 
      id: 1, 
      label: 'Families', 
      icon: '👨‍👩‍👧‍👦', 
      component: Families,
      requiredPermission: 'can_manage_families',
      path: '/entities/families'
    },
    { 
      id: 2, 
      label: 'Properties', 
      icon: '🏠', 
      component: Properties,
      requiredPermission: 'can_view_properties',
      path: '/entities/properties'
    },
    { 
      id: 3, 
      label: 'Loans', 
      icon: '💳', 
      component: Loans,
      requiredPermission: 'can_view_entities',
      path: '/entities/loans'
    },
    { 
      id: 4, 
      label: 'Bank Accounts', 
      icon: '🏦', 
      component: BankAccounts,
      requiredPermission: 'can_view_balances',
      path: '/entities/accounts'
    },
    { 
      id: 5, 
      label: 'Companies', 
      icon: '🏢', 
      component: Companies,
      requiredPermission: 'can_view_entities',
      path: '/entities/companies'
    },
    {
      id: 6,
      label: 'Pensions',
      icon: '🛡️',
      component: Pensions,
      requiredPermission: 'can_view_entities',
      path: '/entities/pensions'
    }
  ], []);

  // Sync tab value with URL
  useEffect(() => {
    const path = location.pathname;
    const tabIndex = allTabs.findIndex(tab => tab.path === path);
    if (tabIndex !== -1) {
      setActiveTab(tabIndex);
    } else if (path === '/entities') {
      // Default to first available tab
      const firstTab = allTabs.find(tab => {
        if (!user) return false;
        if (user.role === 'admin') return true;
        return user[tab.requiredPermission as keyof typeof user] === true;
      });
      if (firstTab) {
        navigate(firstTab.path, { replace: true });
      }
    }
  }, [location.pathname, navigate, allTabs, user]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const selectedTab = allTabs[newValue];
    if (selectedTab) {
      navigate(selectedTab.path);
    }
  };

  // Filter tabs based on user permissions
  const tabs = allTabs.filter(tab => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admins see everything
    return user[tab.requiredPermission as keyof typeof user] === true;
  });

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  // If user has no permissions, show access denied message
  if (!user || (user.role !== 'admin' && tabs.length === 0)) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            📊 Entities
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Access Restricted
            </Typography>
            <Typography variant="body2">
              You don't have permission to view any entities. Please contact your administrator to request access.
            </Typography>
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleChange} 
              aria-label="entities tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{tab.icon}</span>
                      <span>{tab.label}</span>
                    </Box>
                  }
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </Box>

          {tabs.map((tab, index) => (
            <TabPanel key={tab.id} value={activeTab} index={index}>
              {ActiveComponent && <ActiveComponent />}
            </TabPanel>
          ))}
      </Paper>
    </Container>
  );
};
